import { gql } from '@apollo/client';
import PropTypes from 'prop-types';
import { createModelSegment } from 'client/data/luckdragon/segment';
import { EdmundsGraphQLFederation } from 'client/data/graphql/graphql-client';
import { withMetrics } from 'client/data/api/api-metrics';
import { getPubStatesObjectFromArray, isPreProd } from 'site-modules/shared/utils/publication-states';
import { getParamsFromVehicle } from 'site-modules/shared/utils/core-page/params';
import {
  buildTrimSummariesPath,
  InventoryCoreConfigurationsModel,
} from 'client/data/models/inventory-core-configurations';
import { getSupportedGraphqlModelYear } from './vehicle-model-year';

const DEFAULT = 'default';

export const VEHICLE_DATA = {
  TRIM_SUMMARY: 'trim_summary',
};

export function getVehicleDataToResolvePath() {
  return 'vehicleDataToResolve';
}

export function buildVehicleDataPath(contentVehicle) {
  return (
    !!contentVehicle?.make &&
    !!contentVehicle?.model &&
    `makes["${contentVehicle.make}"].models["${contentVehicle.model}"].years["${contentVehicle?.year || DEFAULT}"].vehicleData`
  );
}

export const VehicleEditorialEntities = {
  vehicle: PropTypes.shape({
    make: PropTypes.shape({
      id: PropTypes.number,
      slug: PropTypes.string,
      name: PropTypes.string,
    }),
    model: PropTypes.shape({
      modelLinkCode: PropTypes.string,
      slug: PropTypes.string,
      name: PropTypes.string,
      publicationStates: PropTypes.arrayOf(PropTypes.string),
    }),
    submodels: PropTypes.shape({
      slug: PropTypes.string,
      name: PropTypes.string,
    }),
    year: PropTypes.string,
    publicationStates: PropTypes.arrayOf(PropTypes.string),
    modelYearId: PropTypes.number,
  }),
};

export const VehicleEditorialModel = createModelSegment('vehicleEditorial', [
  {
    path: 'vehicleDataToResolve',
  },
  /**
   * @see buildVehicleDataPath
   */
  {
    path: 'makes["{make}"].models["{model}"].years["{year}"].vehicleData',
    async resolve({ make, model, year }, context) {
      const modelYear = getSupportedGraphqlModelYear(year);
      if (year !== DEFAULT && modelYear === null) {
        return null;
      }

      return withMetrics(EdmundsGraphQLFederation, context)
        .query(
          gql`
            query ($make: String!, $model: String!, $year: Int!, $hasYear: Boolean!) {
              makes(makeSlug: $make) {
                id
                name
                models(modelSlug: $model) {
                  name
                  modelLinkCodes
                  publicationStates
                }
              }
              modelYears(makeSlug: $make, modelSlug: $model, year: $year) @include(if: $hasYear) {
                id
                publicationStates
                submodels(sortBy: { shareBySales: DESC }, page: { pageSize: 1 }) {
                  name
                  slug
                }
              }
            }
          `,
          {
            make,
            model,
            year: modelYear || 0,
            hasYear: year !== DEFAULT,
          }
        )
        .then(response => ({
          make: {
            id: parseInt(response?.makes?.[0]?.id, 10),
            slug: make,
            name: response?.makes?.[0]?.name,
          },
          model: {
            slug: model,
            name: response?.makes?.[0]?.models?.[0]?.name,
            modelLinkCode: response?.makes?.[0]?.models?.[0]?.modelLinkCodes?.[0],
            pubStates: getPubStatesObjectFromArray(response?.makes?.[0]?.models?.[0]?.publicationStates),
          },
          submodels: {
            name: response?.modelYears?.[0]?.submodels?.[0]?.name,
            slug: response?.modelYears?.[0]?.submodels?.[0]?.slug,
          },
          pubStates: getPubStatesObjectFromArray(response?.modelYears?.[0]?.publicationStates),
          year: year === DEFAULT ? undefined : year,
          modelYearId: parseInt(response?.modelYears?.[0]?.id, 10),
        }))
        .then(async vehicle => {
          if (year === DEFAULT || isPreProd(vehicle?.pubStates)) {
            return vehicle;
          }

          const vehicleDataToResolve = context.getValue(getVehicleDataToResolvePath());
          const vehicleParams = getParamsFromVehicle(vehicle);

          const vehicleDataPromises = vehicleDataToResolve?.map?.(dataToResolve => {
            switch (dataToResolve) {
              case VEHICLE_DATA.TRIM_SUMMARY: {
                return context.resolveValue(
                  buildTrimSummariesPath(vehicleParams),
                  InventoryCoreConfigurationsModel,
                  false
                );
              }
              default: {
                return Promise.resolve();
              }
            }
          });

          await Promise.all(vehicleDataPromises || []);

          return vehicle;
        });
    },
  },
]);
